/**
 * DriverForm - Form component for creating/editing drivers
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Grid,
} from "@mui/material";
import { createDriver, updateDriver } from "../services/driverService";
import { getPartner } from "../../partners/services/partnerService";
import PartnerSearchAutocomplete from "../../../shared/components/PartnerSearchAutocomplete";

/**
 * DriverForm Component
 */
const DriverForm = ({ open, driver, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    licenseNumber: "",
    licenseExpiry: null,
    ownerType: "bubakii",
    partnerId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(false);

  // Load driver data when editing
  useEffect(() => {
    if (driver) {
      setFormData({
        name: driver.name || "",
        phone: driver.phone || "",
        email: driver.email || "",
        licenseNumber: driver.licenseNumber || "",
        licenseExpiry: driver.licenseExpiry ? new Date(driver.licenseExpiry) : null,
        ownerType: driver.ownerType || "bubakii",
        partnerId: driver.partnerId || "",
      });
    } else {
      setFormData({
        name: "",
        phone: "",
        email: "",
        licenseNumber: "",
        licenseExpiry: null,
        ownerType: "bubakii",
        partnerId: "",
      });
    }
    setError(null);
    setSelectedPartner(null);
  }, [driver, open]);

  // Load partner when editing a driver with partnerId
  useEffect(() => {
    const loadPartner = async () => {
      if (formData.ownerType === "third_party" && formData.partnerId) {
        setLoadingPartner(true);
        try {
          const result = await getPartner(formData.partnerId);
          if (result.success) {
            setSelectedPartner(result.data);
          }
        } catch (err) {
          console.error("Error loading partner:", err);
          setSelectedPartner(null);
        } finally {
          setLoadingPartner(false);
        }
      } else {
        setSelectedPartner(null);
      }
    };
    loadPartner();
  }, [formData.partnerId, formData.ownerType]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "ownerType" && value !== "third_party") {
      setFormData((prev) => ({ ...prev, partnerId: "" }));
    }
    setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }
    if (!formData.phone.trim()) {
      setError("Phone is required");
      return false;
    }
    if (!formData.licenseNumber.trim()) {
      setError("License number is required");
      return false;
    }
    if (!formData.licenseExpiry) {
      setError("License expiry date is required");
      return false;
    }
    if (formData.ownerType === "third_party" && !formData.partnerId) {
      setError("Partner is required for 3rd party drivers");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        licenseExpiry: formData.licenseExpiry.toISOString(),
        partnerId: formData.ownerType === "third_party" ? formData.partnerId : undefined,
      };

      const result = driver
        ? await updateDriver(driver.id, submitData)
        : await createDriver(submitData);

      if (result.success) {
        onSave();
      } else {
        setError(result.error || "Failed to save driver");
      }
    } catch (err) {
      setError(err.message || "Failed to save driver");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{driver ? "Edit Driver" : "Create Driver"}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="License Number"
                value={formData.licenseNumber}
                onChange={(e) => handleChange("licenseNumber", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="License Expiry"
                type="date"
                value={
                  formData.licenseExpiry
                    ? new Date(formData.licenseExpiry).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  handleChange("licenseExpiry", e.target.value ? new Date(e.target.value) : null)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Owner Type</InputLabel>
                <Select
                  value={formData.ownerType}
                  label="Owner Type"
                  onChange={(e) => handleChange("ownerType", e.target.value)}
                >
                  <MenuItem value="bubakii">Bubakii</MenuItem>
                  <MenuItem value="third_party">3rd Party</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.ownerType === "third_party" && (
              <Grid item xs={12}>
                <PartnerSearchAutocomplete
                  value={selectedPartner || null}
                  onChange={(partner) => {
                    if (partner) {
                      handleChange("partnerId", partner.id);
                      setSelectedPartner(partner);
                    } else {
                      handleChange("partnerId", "");
                      setSelectedPartner(null);
                    }
                  }}
                  label="Partner"
                  required
                  disabled={loadingPartner}
                  error={error && formData.ownerType === "third_party" && !formData.partnerId ? "Partner is required" : null}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : driver ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverForm;

